/**
 * Onglet Import et paramètres — EF-NAV-001, EF-NAV-002, EF-NAV-007.
 *
 * Il se lit de haut en bas : le parcours, puis les réglages qui pilotent la
 * recherche et le calcul, puis les deux actions qui les consomment. La section
 * de maintenance ferme la marche, repliée.
 */

import type { JSX } from 'preact';
import { useEffect, useRef } from 'preact/hooks';
import { validateParameters } from '../../core/parameters';
import { isOffline } from '../../platform/offline';
import { Disclosure } from '../components/Disclosure';
import { Glyph } from '../icons';
import { ACTIONS, ERRORS, MARKERS, SCREENS } from '../labels';
import { ComputeParametersSection } from '../sections/ComputeParametersSection';
import { MaintenanceSection } from '../sections/MaintenanceSection';
import { RouteSection } from '../sections/RouteSection';
import { SearchParametersSection } from '../sections/SearchParametersSection';
import { formatDistance } from '../format';
import {
  busy,
  chooseParametersFile,
  computeNow,
  document_,
  exportParameters,
  parameters,
  readOnly,
  startSearch,
} from '../state';

export function SetupScreen(): JSX.Element {
  const scroller = useRef<HTMLDivElement>(null);
  const current = document_.value;

  // Cet onglet s'ouvre à son sommet à chaque activation (EF-NAV-007).
  useEffect(() => {
    scroller.current?.scrollTo?.(0, 0);
  }, []);

  const offline = isOffline();
  const searching = busy.value === 'recherche de POI';
  const validation = validateParameters(parameters.value, current?.route.track.lengthMeters ?? null);
  const invalid = validation.issues.length > 0;

  return (
    <div class="screen screen--scroll" ref={scroller}>
      <h1 class="screen__title">{SCREENS.setup}</h1>

      <Disclosure
        title="Parcours"
        id="setup-parcours"
        hint={current === null ? 'aucun parcours' : formatDistance(current.route.track.lengthMeters)}
      >
        <RouteSection />
      </Disclosure>

      <Disclosure title="Paramètres" id="setup-parametres">
        <div class="actions">
          <button
            type="button"
            disabled={readOnly.value}
            aria-label={ACTIONS.importParameters}
            onClick={() => {
              void chooseParametersFile();
            }}
          >
            <Glyph name="importer" label="" size={22} decorative />
          </button>
          <button
            type="button"
            aria-label={ACTIONS.exportParameters}
            onClick={() => {
              void exportParameters();
            }}
          >
            <Glyph name="exporter" label="" size={22} decorative />
          </button>
        </div>

        <div class="subsection">
          <h2>Recherche</h2>
          <SearchParametersSection />
        </div>

        <div class="subsection">
          <h2>Calcul du temps</h2>
          <ComputeParametersSection />
        </div>
      </Disclosure>

      {/* Les deux seules actions principales de cet onglet. */}
      <div class="actions actions--primary">
        <button
          type="button"
          class="primary"
          disabled={current === null || readOnly.value || offline || searching || invalid}
          onClick={() => {
            void startSearch();
          }}
        >
          <Glyph name="loupe" label="" size={22} decorative />
          {ACTIONS.search}
        </button>
        <button
          type="button"
          class="primary"
          disabled={current === null || readOnly.value || invalid}
          onClick={() => {
            void computeNow();
          }}
        >
          <Glyph name="calculer" label="" size={22} decorative />
          {ACTIONS.compute}
        </button>
      </div>

      {invalid ? <p class="notice">{ERRORS.invalidParameters}</p> : null}
      {offline ? <p class="notice">{ERRORS.searchOffline}</p> : null}
      {readOnly.value ? <p class="notice">{MARKERS.readOnly}</p> : null}

      <Disclosure title="À propos et maintenance" id="setup-about">
        <MaintenanceSection />
      </Disclosure>
    </div>
  );
}
